import {
  Box,
  Divider,
  Group,
  Kbd,
  Loader,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue, useHotkeys, useLocalStorage } from '@mantine/hooks';
import { Badge } from '@vrplatform/components';
import {
  useApiQuery,
  useMe,
  useTeam,
  useTeamRole,
  useTrackingEvent,
} from '@vrplatform/data';
import { HiddenFeatureIndicator } from '@vrplatform/embed/components';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import {
  showErrorNotification,
  showSuccessNotification,
} from '@vrplatform/theme';
import type {
  QuickSearchCloseOutcome,
  QuickSearchTrigger,
} from '@vrplatform/tracking';
import { toTitleCase } from '@vrplatform/utils';
import {
  createContext,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import {
  QUICK_SEARCH_TABS,
  type QuickSearchTab,
} from './QuickSearch.constants';
import {
  type QuickSearchProductResult,
  useQuickSearchResults,
} from './useQuickSearchResults';

type QuickSearchContextValue = {
  close: (outcome?: QuickSearchCloseOutcome) => void;
  open: (trigger: QuickSearchTrigger) => void;
};

// Tracking state of one open modal; reported with quick_search_closed.
type QuickSearchSession = {
  id: string;
  openedAt: number;
  settledQueries: Set<string>;
  zeroResultQueries: Set<string>;
  erroredQueries: Set<string>;
  tabsUsed: Set<QuickSearchTab>;
  lastQueryLength: number;
};

type QuickSearchActionVisibility = 'super-admin' | 'vrp-admin';

export type QuickSearchAction = {
  id: string;
  label: string;
  description?: string;
  group?: string;
  icon?: IconDefinition;
  leftSection?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  visibility?: QuickSearchActionVisibility;
};

const QuickSearchContext = createContext<QuickSearchContextValue | null>(null);
const QuickSearchActionsContext = createContext<QuickSearchAction[]>([]);

export const QuickSearchProvider = ({ children }: { children: ReactNode }) => {
  const [opened, setOpened] = useState(false);
  const sessionRef = useRef<QuickSearchSession | null>(null);
  const actions = useContext(QuickSearchActionsContext);
  const theme = useMantineTheme();
  const track = useTrackingEvent();
  const { id: userId } = useMe();
  const [{ id: teamId }] = useTeam();
  const [recentProductResults, setRecentProductResults] = useLocalStorage<
    QuickSearchProductResult[]
  >({
    key: `quick-search-recent:v1:${userId}:${teamId}`,
    defaultValue: [],
  });
  const open = useCallback(
    (trigger: QuickSearchTrigger) => {
      if (sessionRef.current) return;

      sessionRef.current = {
        id: crypto.randomUUID(),
        openedAt: Date.now(),
        settledQueries: new Set(),
        zeroResultQueries: new Set(),
        erroredQueries: new Set(),
        tabsUsed: new Set(['all']),
        lastQueryLength: 0,
      };
      setOpened(true);
      void track('quick_search_opened', {
        searchSessionId: sessionRef.current.id,
        trigger,
        recentCount: recentProductResults.length,
      });
    },
    [recentProductResults.length, track]
  );
  const close = useCallback(
    (outcome: QuickSearchCloseOutcome = 'dismissed') => {
      const session = sessionRef.current;
      if (!session) return;

      sessionRef.current = null;
      setOpened(false);
      void track('quick_search_closed', {
        searchSessionId: session.id,
        outcome,
        durationMs: Date.now() - session.openedAt,
        queryCount: session.settledQueries.size,
        zeroResultQueryCount: session.zeroResultQueries.size,
        errorCount: session.erroredQueries.size,
        tabsUsed: [...session.tabsUsed],
        lastQueryLength: session.lastQueryLength,
      });
    },
    [track]
  );

  useHotkeys(
    [['mod+K', () => open('shortcut')]],
    ['INPUT', 'TEXTAREA', 'SELECT']
  );

  const value = useMemo(
    () => ({
      close,
      open,
    }),
    [close, open]
  );

  return (
    <QuickSearchContext.Provider value={value}>
      {children}
      <Modal
        opened={opened}
        onClose={() => close()}
        withCloseButton={false}
        padding={0}
        radius={8}
        size={860}
        aria-label="Search"
        overlayProps={{
          backgroundOpacity: 0.25,
          blur: 4,
          color: '#000',
        }}
        styles={{
          content: {
            height: 'min(500px, calc(100vh - 160px))',
            overflow: 'hidden',
          },
          inner: {
            alignItems: 'flex-start',
            paddingTop: 130,

            [`@media (max-width: ${theme.breakpoints.sm})`]: {
              paddingTop: 72,
            },
          },
        }}
      >
        <QuickSearch
          actions={actions}
          recentProductResults={recentProductResults}
          sessionRef={sessionRef}
          setRecentProductResults={setRecentProductResults}
        />
      </Modal>
    </QuickSearchContext.Provider>
  );
};

export function useQuickSearch() {
  const context = useContext(QuickSearchContext);

  if (!context) {
    throw new Error('useQuickSearch must be used inside QuickSearchProvider');
  }

  return context;
}

export const QuickSearch = ({
  actions,
  recentProductResults,
  sessionRef,
  setRecentProductResults,
}: {
  actions: QuickSearchAction[];
  recentProductResults: QuickSearchProductResult[];
  sessionRef: RefObject<QuickSearchSession | null>;
  setRecentProductResults: (
    update: (current: QuickSearchProductResult[]) => QuickSearchProductResult[]
  ) => void;
}) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<QuickSearchTab>('all');
  const [debouncedQuery] = useDebouncedValue(query, 250);
  const { close } = useQuickSearch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { isVrpAdmin } = useTeamRole();
  const [{ id: teamId }] = useTeam();
  const track = useTrackingEvent();

  const trimmedQuery = query.trim();
  const searchQuery = trimmedQuery.toLowerCase();
  const debouncedSearchQuery = debouncedQuery.trim();
  const visibleTabs = QUICK_SEARCH_TABS.filter(
    (tab) => tab.value !== 'commands' || isVrpAdmin
  );
  const selectedTab =
    activeTab === 'commands' && !isVrpAdmin ? 'all' : activeTab;
  const billingQuery = useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/team', { signal });

      if (result.error) throw result.error;

      return {
        customerId: result.data.billingCustomerId,
        billingPlan: result.data.billingPlan,
        subscriptionStatus: result.data.billingSubscriptionStatus,
        billingPartnerName: result.data.billingPartner.name,
        classification: result.data.isTest ? 'Test / Demo' : 'Regular Team',
      };
    },
    {
      queryGroup: 'teams',
      uniqueQueryKey: 'quick-search-billing',
      variables: { teamId },
      skip: selectedTab !== 'commands' || !isVrpAdmin || !teamId,
      hideNotification: true,
    }
  );
  const quickSearchResultsQuery = useQuickSearchResults({
    query: selectedTab === 'commands' ? '' : debouncedSearchQuery,
  });
  const visibleProductResults = useMemo(() => {
    if (selectedTab === 'commands') return [];
    if (!searchQuery) return selectedTab === 'all' ? recentProductResults : [];

    const results =
      quickSearchResultsQuery.data?.pages.flatMap((page) => page.results) ?? [];

    return selectedTab === 'all'
      ? results
      : results.filter((result) => result.type === selectedTab);
  }, [
    quickSearchResultsQuery.data,
    recentProductResults,
    searchQuery,
    selectedTab,
  ]);
  useEffect(() => {
    const session = sessionRef.current;
    if (
      !session ||
      selectedTab === 'commands' ||
      debouncedSearchQuery.length < 2 ||
      quickSearchResultsQuery.isPlaceholderData
    )
      return;

    if (quickSearchResultsQuery.error) {
      session.erroredQueries.add(debouncedSearchQuery);
      return;
    }

    const firstPage = quickSearchResultsQuery.data?.pages[0];
    if (!firstPage) return;

    session.settledQueries.add(debouncedSearchQuery);
    if (!Object.values(firstPage.counts).some(Boolean))
      session.zeroResultQueries.add(debouncedSearchQuery);
  }, [
    debouncedSearchQuery,
    quickSearchResultsQuery.data,
    quickSearchResultsQuery.error,
    quickSearchResultsQuery.isPlaceholderData,
    selectedTab,
    sessionRef,
  ]);
  const isProductSearchLoading =
    (selectedTab !== 'commands' &&
      trimmedQuery.length >= 2 &&
      debouncedSearchQuery !== trimmedQuery) ||
    quickSearchResultsQuery.isLoading ||
    quickSearchResultsQuery.isFetching;

  const visibleActions = useMemo(() => {
    if (selectedTab !== 'commands') return [];

    const commandActions = actions.filter((action) => action.id !== 'home');
    const customerId = billingQuery.data?.customerId;

    if (customerId && !billingQuery.error) {
      commandActions.push({
        id: 'copy-billing-customer-id',
        label: 'Copy Billing Customer ID',
        description: customerId,
        group: 'Billing Hyperline',
        icon: 'CopyIcon',
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(customerId);
            showSuccessNotification({
              title: 'Copied',
              message: 'Billing customer ID copied to clipboard.',
            });
          } catch {
            showErrorNotification({
              title: 'Failed to copy',
              message: 'Could not copy the billing customer ID.',
            });
          }
        },
      });
    }

    if (billingQuery.data && !billingQuery.error) {
      commandActions.push(
        {
          id: 'billing-plan',
          label: 'Billing Plan',
          description: billingQuery.data.billingPlan || 'Not configured',
          group: 'Billing',
          icon: 'Tag2Icon',
        },
        {
          id: 'billing-subscription-status',
          label: 'Subscription Status',
          description: billingQuery.data.subscriptionStatus
            ? toTitleCase(billingQuery.data.subscriptionStatus)
            : 'Not configured',
          group: 'Billing',
          icon: 'Tag2Icon',
        },
        {
          id: 'billing-partner',
          label: 'Billing Partner',
          description: billingQuery.data.billingPartnerName,
          group: 'Billing',
          icon: 'UsersIcon',
        },
        {
          id: 'team-classification',
          label: 'Team Classification',
          description: billingQuery.data.classification,
          group: 'Billing',
          icon: 'Tag2Icon',
        }
      );
    }

    return searchQuery
      ? commandActions.filter((action) => {
          return (
            action.label.toLowerCase().includes(searchQuery) ||
            (action.description?.toLowerCase() ?? '').includes(searchQuery) ||
            (action.group?.toLowerCase() ?? '').includes(searchQuery)
          );
        })
      : commandActions;
  }, [
    actions,
    billingQuery.data,
    billingQuery.error,
    searchQuery,
    selectedTab,
  ]);

  return (
    <Box>
      <Box px={12} py={8}>
        <TextInput
          value={query}
          onChange={(event) => {
            const session = sessionRef.current;
            if (session)
              session.lastQueryLength = event.currentTarget.value.trim().length;
            setQuery(event.currentTarget.value);
          }}
          placeholder="Search..."
          aria-label="Search"
          variant="unstyled"
          size="md"
          data-autofocus
          leftSection={
            <Icon icon="SearchIcon" size={18} color={theme.colors.neutral[5]} />
          }
          leftSectionWidth={30}
          rightSection={
            isProductSearchLoading ? (
              <Loader
                aria-label="Loading search results"
                color="neutral.5"
                role="status"
                size="xs"
              />
            ) : undefined
          }
          rightSectionWidth={34}
        />
      </Box>
      <Divider />

      <Tabs
        value={selectedTab}
        onChange={(value) => {
          const tab = (value ?? 'all') as QuickSearchTab;
          sessionRef.current?.tabsUsed.add(tab);
          setActiveTab(tab);
        }}
        keepMounted={false}
      >
        <Tabs.List
          px={12}
          style={{
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          {visibleTabs.map((tab) => (
            <Tabs.Tab
              key={tab.value}
              value={tab.value}
              aria-label={tab.value === 'commands' ? tab.label : undefined}
              ml={'align' in tab && tab.align === 'right' ? 'auto' : undefined}
              px="xs"
            >
              {tab.value === 'commands' ? (
                <HiddenFeatureIndicator
                  permission="vrp-admin"
                  customTooltip="Commands - VRP Admin only"
                  display="inline-flex"
                  p={2}
                >
                  <Icon icon={tab.icon} size={14} />
                </HiddenFeatureIndicator>
              ) : tab.value === 'all' && !searchQuery ? (
                'Recent'
              ) : selectedTab !== 'commands' &&
                trimmedQuery.length >= 2 &&
                debouncedSearchQuery === trimmedQuery &&
                quickSearchResultsQuery.isSuccess &&
                quickSearchResultsQuery.data?.pages[0] ? (
                `${tab.label} (${
                  tab.value === 'all'
                    ? Object.values(
                        quickSearchResultsQuery.data.pages[0].counts
                      ).reduce((total, count) => total + count, 0)
                    : (quickSearchResultsQuery.data.pages[0].counts[
                        tab.value
                      ] ?? 0)
                })`
              ) : (
                tab.label
              )}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {visibleTabs.map((tab) => (
          <Tabs.Panel
            key={tab.value}
            value={tab.value}
            h={390}
            pos="relative"
            renderRoot={(props) => (
              <ScrollArea
                {...props}
                scrollbars="y"
                viewportProps={{
                  'aria-label': 'Search results',
                  role: 'region',
                }}
                onBottomReached={() => {
                  if (
                    quickSearchResultsQuery.hasNextPage &&
                    !quickSearchResultsQuery.isFetchingNextPage
                  ) {
                    void quickSearchResultsQuery.fetchNextPage();
                  }
                }}
              />
            )}
          >
            <LoadingOverlay
              visible={isProductSearchLoading}
              aria-label="Loading result list"
              role="status"
            />
            {selectedTab === 'commands' &&
              (billingQuery.isLoading ? (
                <Group
                  px={16}
                  py="sm"
                  role="status"
                  aria-label="Loading billing details"
                >
                  <Loader size="xs" />
                  <Text size="sm" c="neutral.5">
                    Loading billing details...
                  </Text>
                </Group>
              ) : billingQuery.error ? (
                <Group px={16} py="sm">
                  <Text size="sm" c="red" role="alert">
                    Could not load billing details.
                  </Text>
                  <UnstyledButton onClick={() => void billingQuery.refetch()}>
                    <Text size="sm" c="primary.6">
                      Retry billing details
                    </Text>
                  </UnstyledButton>
                </Group>
              ) : billingQuery.data && !billingQuery.data.customerId ? (
                <Text px={16} py="sm" size="sm" c="neutral.5">
                  No billing customer ID configured for this team.
                </Text>
              ) : null)}
            {visibleProductResults.length > 0 ? (
              <Stack gap={0}>
                {visibleProductResults.map((result, position) => {
                  const matchStart = searchQuery
                    ? result.label.toLowerCase().indexOf(searchQuery)
                    : -1;
                  const matchEnd = matchStart + searchQuery.length;

                  return (
                    <UnstyledButton
                      key={result.id}
                      onClick={() => {
                        const session = sessionRef.current;
                        if (session && selectedTab !== 'commands')
                          void track('quick_search_result_selected', {
                            searchSessionId: session.id,
                            resultType: result.type,
                            position,
                            msSinceOpen: Date.now() - session.openedAt,
                            ...(searchQuery
                              ? {
                                  source: 'search',
                                  tab: selectedTab,
                                  queryLength: trimmedQuery.length,
                                  pagesLoaded:
                                    quickSearchResultsQuery.data?.pages
                                      .length ?? 0,
                                }
                              : { source: 'recent', tab: 'all' }),
                          });
                        setRecentProductResults((current) =>
                          [
                            result,
                            ...current.filter(
                              (recentResult) => recentResult.id !== result.id
                            ),
                          ].slice(0, 10)
                        );
                        navigate(result.href);
                        close('resultSelected');
                      }}
                      sx={{
                        borderBottom: `1px solid ${theme.colors.neutral[1]}`,
                        display: 'block',
                        padding: '12px 16px',
                        width: '100%',

                        '&:hover, &:focus-visible': {
                          backgroundColor: theme.colors.neutral[0],
                        },
                      }}
                    >
                      <Group gap={24} wrap="nowrap" align="center">
                        <Group gap={8} wrap="nowrap" w={260}>
                          <Box c="primary.6" sx={{ flexShrink: 0 }}>
                            <Icon
                              icon={result.icon}
                              size={16}
                              color="#1343CC"
                            />
                          </Box>
                          <Text
                            component="span"
                            size="sm"
                            fw={500}
                            c="neutral.8"
                            truncate
                            sx={{ letterSpacing: 0 }}
                          >
                            {matchStart === -1 ? (
                              result.label
                            ) : (
                              <>
                                {result.label.slice(0, matchStart)}
                                <Text span fw={700} inherit>
                                  {result.label.slice(matchStart, matchEnd)}
                                </Text>
                                {result.label.slice(matchEnd)}
                              </>
                            )}
                          </Text>
                        </Group>

                        <Text
                          component="span"
                          size="sm"
                          c="neutral.5"
                          truncate
                          sx={{ flex: 1, letterSpacing: 0 }}
                        >
                          {result.description}
                        </Text>

                        <Badge
                          appearance="tag"
                          leftIcon={result.icon}
                          leftIconColor="#1343CC"
                          sx={{ flexShrink: 0 }}
                        >
                          {result.badge}
                        </Badge>
                      </Group>
                    </UnstyledButton>
                  );
                })}
                {quickSearchResultsQuery.isFetchingNextPage ? (
                  <Group justify="center" py="sm">
                    <Loader
                      aria-label="Loading more search results"
                      color="neutral.5"
                      role="status"
                      size="xs"
                    />
                  </Group>
                ) : null}
              </Stack>
            ) : visibleActions.length > 0 ? (
              <Stack gap={0}>
                {visibleActions.map((action) => {
                  const matchStart = searchQuery
                    ? action.label.toLowerCase().indexOf(searchQuery)
                    : -1;
                  const matchEnd = matchStart + searchQuery.length;
                  const isInteractive = !!action.onClick;

                  return (
                    <UnstyledButton
                      key={action.id}
                      component={isInteractive ? 'button' : 'div'}
                      role={isInteractive ? undefined : 'group'}
                      aria-label={isInteractive ? undefined : action.label}
                      onClick={
                        isInteractive
                          ? (event) => {
                              action.onClick?.(event);
                              close('commandExecuted');
                            }
                          : undefined
                      }
                      sx={{
                        borderBottom: `1px solid ${theme.colors.neutral[1]}`,
                        cursor: isInteractive ? 'pointer' : 'default',
                        display: 'block',
                        padding: '12px 16px',
                        width: '100%',

                        ...(isInteractive && {
                          '&:hover, &:focus-visible': {
                            backgroundColor: theme.colors.neutral[0],
                          },
                        }),
                      }}
                    >
                      <Group gap={24} wrap="nowrap" align="center">
                        <Group gap={8} wrap="nowrap" w={260}>
                          <Box c="primary.6" sx={{ flexShrink: 0 }}>
                            {action.leftSection ?? (
                              <Icon
                                icon={
                                  action.icon ??
                                  QUICK_SEARCH_TABS.find(
                                    (tab) =>
                                      tab.value === action.group?.toLowerCase()
                                  )?.icon ??
                                  (action.group === 'Navigate'
                                    ? 'ArrowRightIcon'
                                    : action.visibility
                                      ? 'Lock2Icon'
                                      : 'SearchIcon')
                                }
                                size={16}
                                color="#1343CC"
                              />
                            )}
                          </Box>
                          <Text
                            component="span"
                            size="sm"
                            fw={500}
                            c="neutral.8"
                            truncate
                            sx={{ letterSpacing: 0 }}
                          >
                            {matchStart === -1 ? (
                              action.label
                            ) : (
                              <>
                                {action.label.slice(0, matchStart)}
                                <Text span fw={700} inherit>
                                  {action.label.slice(matchStart, matchEnd)}
                                </Text>
                                {action.label.slice(matchEnd)}
                              </>
                            )}
                          </Text>
                        </Group>

                        <Text
                          component="span"
                          size="sm"
                          c="neutral.5"
                          truncate
                          sx={{ flex: 1, letterSpacing: 0 }}
                        >
                          {action.description}
                        </Text>

                        {action.visibility === 'super-admin' ? (
                          <Tooltip
                            label="Super Admin only"
                            withArrow
                            withinPortal
                          >
                            <Group
                              gap={4}
                              wrap="nowrap"
                              px={6}
                              py={2}
                              sx={{
                                border: `1.5px solid ${theme.colors.orange[5]}`,
                                borderRadius: 999,
                                flexShrink: 0,
                              }}
                            >
                              <Icon
                                icon="Lock2Icon"
                                size={12}
                                color={theme.colors.orange[5]}
                              />
                              <Text
                                component="span"
                                size="xs"
                                fw={500}
                                c="neutral.6"
                              >
                                Super Admin
                              </Text>
                            </Group>
                          </Tooltip>
                        ) : null}
                      </Group>
                    </UnstyledButton>
                  );
                })}
              </Stack>
            ) : isProductSearchLoading ? null : selectedTab !== 'commands' &&
              quickSearchResultsQuery.error ? (
              <Box px={16} py={24}>
                <Text size="sm" c="neutral.5">
                  Search failed
                </Text>
              </Box>
            ) : selectedTab !== 'commands' &&
              trimmedQuery.length > 0 &&
              trimmedQuery.length < 2 ? (
              <Box px={16} py={24}>
                <Text size="sm" c="neutral.5">
                  Keep typing to search
                </Text>
              </Box>
            ) : searchQuery ? (
              <Box px={16} py={24}>
                <Text size="sm" c="neutral.5">
                  No results
                </Text>
              </Box>
            ) : selectedTab === 'all' ? (
              <Stack
                align="center"
                gap={6}
                inset={0}
                justify="center"
                pos="absolute"
                px={24}
              >
                <ThemeIcon
                  aria-hidden
                  color={theme.primaryColor}
                  radius="xl"
                  size={48}
                  variant="light"
                >
                  <Icon
                    icon="HistoryIcon"
                    size={24}
                    color={theme.colors[theme.primaryColor][6]}
                  />
                </ThemeIcon>
                <Text component="h3" fw={600} mt={4} size="sm" ta="center">
                  No recent items
                </Text>
                <Text c="neutral.5" size="sm" ta="center">
                  Items you open from search will appear here.
                </Text>
              </Stack>
            ) : null}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Box>
  );
};

export const QuickSearchShortcutBadge = () => <Kbd size="xs">⌘K</Kbd>;

export const QuickSearchActionsProvider = ({
  actions,
  children,
}: {
  actions: QuickSearchAction[];
  children: ReactNode;
}) => (
  <QuickSearchActionsContext.Provider value={actions}>
    {children}
  </QuickSearchActionsContext.Provider>
);
